import useVersionManagementV1 from './useVersionManagementV1';

/**
 * Custom hook to manage user role versions
 * Stores and tracks different versions of user roles from regenerations
 */
const useUserRoleVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // Helper function to parse user roles from content
  const parseUserRoles = (content) => {
    if (!content || !content.trim()) return [];
    
    // Minimal cleaning - just use the content as is
    const cleanedContent = content;
    
    try {
      // Try to parse as JSON first
      const jsonData = JSON.parse(cleanedContent);
      if (Array.isArray(jsonData)) {
        return [{ title: 'User Roles', roles: jsonData }];
      }
    } catch (e) {
      // Not JSON, parse markdown
      const roles = [];
      
      // Try numbered role pattern: "# 1. **Role Name**" or "## 1. Role Name"
      const numberedRolePattern = /#\s+(\d+)\.\s*\*\*([^*]+)\*\*|#\s+(\d+)\.\s*([^#\n]+)/g;
      let matches = [];
      let match;
      
      while ((match = numberedRolePattern.exec(cleanedContent)) !== null) {
        const title = (match[2] || match[4]).trim();
        const roleNumber = match[1] || match[3];
        
        // Find the content until the next role or horizontal rule
        const startPos = match.index + match[0].length;
        const nextRoleMatch = numberedRolePattern.exec(cleanedContent);
        numberedRolePattern.lastIndex = match.index + match[0].length; // Reset for next iteration
        
        const endPos = nextRoleMatch ? nextRoleMatch.index : cleanedContent.length;
        const content = cleanedContent.substring(startPos, endPos).trim();
        
        // Only include if content contains role-specific sections
        if (content.includes('Description') || content.includes('Relationship') || 
            content.includes('Workflows') || content.length > 50) {
          matches.push({
            title: `${roleNumber}. ${title}`,
            content: content,
            rawContent: match[0] + '\n' + content
          });
        }
      }
      
      if (matches.length > 0) {
        return [{ title: 'User Roles', roles: matches }];
      }
      
      // Try alternative pattern: look for roles separated by horizontal rules
      const rolesByHorizontalRule = cleanedContent.split(/\n---+\n/).filter(section => section.trim());
      
      if (rolesByHorizontalRule.length > 1) {
        const parsedRoles = rolesByHorizontalRule.map((section, index) => {
          const lines = section.split('\n');
          
          // Find the role title
          let title = `Role ${index + 1}`;
          for (const line of lines) {
            const cleanLine = line.trim();
            if (cleanLine.match(/^#\s+\d+\.\s*\*\*.*\*\*/) || 
                cleanLine.match(/^##\s+\d+\.\s*/) || 
                cleanLine.match(/^\d+\.\s*\*\*.*\*\*/)) {
              title = cleanLine.replace(/^#+\s*/, '').replace(/^\d+\.\s*/, '').replace(/\*\*/g, '').trim();
              break;
            }
          }
          
          return {
            title: title,
            content: section.trim(),
            rawContent: section
          };
        });
        
        return [{ title: 'User Roles', roles: parsedRoles }];
      }
      
      // Fallback: try to find any ## headers
      const headerPattern = /^##\s+(?!\s*(?:Description|Relationship|Main\s+Workflows|Primary\s+Responsibilities))([^#\n]+)(?:\n|$)([\s\S]*?)(?=^##\s+|$)/gm;
      const headerMatches = [];
      let headerMatch;
      
      while ((headerMatch = headerPattern.exec(cleanedContent)) !== null) {
        const title = headerMatch[1].trim();
        const content = headerMatch[2].trim();
        
        if (content.length > 30) {
          headerMatches.push({
            title: title,
            content: content,
            rawContent: headerMatch[0]
          });
        }
      }
      
      if (headerMatches.length > 0) {
        return [{ title: 'User Roles', roles: headerMatches }];
      }
      
      // Final fallback
      return [{
        title: 'User Roles',
        roles: [{
          title: 'User Roles',
          description: cleanedContent,
          content: cleanedContent
        }]
      }];
    }
  };
  
  // Configuration for user role version management
  const roleConfig = {
    // Determine if a message contains user roles
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      // Simplified detection like features - just look for clear role markers
      return (content.includes('## 1.') || content.includes('# 1.') || content.includes('### 1.')) && 
             (content.includes('Description:') || content.includes('Responsibilities:'));
    },
    
    // Parse user roles from content
    // Return flat roles array so useVersionManagementV1's hasMinimumSections check works
    // (it requires parsedContent.length >= 2, but parseUserRoles wraps in single-element array)
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      const parsed = parseUserRoles(content);
      if (parsed.length > 0 && parsed[0].roles) {
        return parsed[0].roles;
      }
      return parsed;
    },
    
    // Extract version information from a message
    extractVersions: (msg, parsedRoles, versionNumber, isPreview = false) => {
      // Don't create a version if we have no valid roles or only fallback content
      if (!parsedRoles || parsedRoles.length === 0) {
        return null;
      }

      // Check if it's just fallback content (single role with generic title and no structured content)
      if (parsedRoles.length === 1) {
        const firstRole = parsedRoles[0];
        if (firstRole.title === 'User Roles' && !firstRole.content?.includes('## ')) {
          return null;
        }
      }
      
      // Use the messageId that was already generated in useVersionManagement
      const versionId = msg._id || msg.id || (isPreview ? 'preview-streaming' : `version-${versionNumber}`);
      
      return {
        id: versionId,
        timestamp: msg.createdAt || new Date().toISOString(),
        roles: parsedRoles,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },
    
    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  // Use the fixed version management hook (V1)
  const versionData = useVersionManagementV1(roleConfig);
  
  // Map the generic version data to role-specific structure
  return {
    versions: versionData.versions,
    currentVersion: versionData.currentVersion ? {
      ...versionData.currentVersion,
      roles: versionData.currentVersion.parsedContent || versionData.currentVersion.roles || []
    } : null,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useUserRoleVersions;